import type { KeyboardEvent, MouseEvent } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import type {
  FontValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { copyTextToClipboard, getColorWithAlpha } from '@elfsight/lib-utils';
import { Popout } from '@elfsight/widget-modal';
import { roundTo4, to4VTuple } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { ellipsisMixin } from '../mixins';

const RESET_TIMEOUT = 3000;

export interface CouponProps {
  children: string;
  color?: string;
  font?: TextStyleValue;
  fontFamily?: FontValue;
  fontColor?: string;
  borderRadius?: number;
  borderColor?: string;
  backgroundColor?: string;
  backgroundHoverColor?: string;
  width?: number | string;
  height?: number;
  messages?: Record<string, string>;
  onCopied?: (value: string) => void;
}

export const Coupon = forwardRef<HTMLDivElement, CouponProps>(
  (
    {
      children,
      color = 'rgb(17, 17, 17)',
      font = {
        fontSize: 16,
        fontWeight: 'bold',
        fontStyle: 'normal'
      },
      fontFamily,
      fontColor,
      borderRadius = 2,
      borderColor: derivedBorderColor,
      backgroundColor: derivedBackgroundColor,
      backgroundHoverColor: derivedBackgroundHoverColor,
      height,
      width,
      messages = {
        copied: 'Copied!',
        clickToCopy: 'Click To Copy'
      },
      onCopied
    },
    forwardingRef
  ) => {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const mergedRef = useMergedRef(forwardingRef, setElement);

    const [isCopied, setCopied] = useState(false);

    const [hover, setHover] = useState(false);
    const [focus, setFocus] = useState(false);
    const isInteracting = hover || focus;

    const { fontWeight, fontStyle, fontSize } = font;

    const currentFontColor = fontColor || color;
    const currentBorderColor =
      derivedBorderColor || getColorWithAlpha(color, 0.5);
    const currentBackgroundColor =
      derivedBackgroundColor || getColorWithAlpha(color, 0.05);
    const currentBackgroundHoverColor =
      derivedBackgroundHoverColor || getColorWithAlpha(color, 0.1);

    const [paddingTop, paddingRight, paddingBottom, paddingLeft] = to4VTuple([
      roundTo4(fontSize / 1.5),
      roundTo4(fontSize / 2)
    ]);

    const copy = (text: string | number) => {
      const str = text.toString();
      copyTextToClipboard(str);
      setCopied(true);

      if (element) {
        element.blur();
      }

      onCopied?.(str);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        copy(children);
      }
    };

    const handleClick = (e: MouseEvent) => {
      e.stopPropagation();
      copy(children);
    };

    useEffect(() => {
      let timeout: number;

      if (isCopied) {
        timeout = window.setTimeout(() => setCopied(false), RESET_TIMEOUT);
      }

      return () => clearTimeout(timeout);
    }, [isCopied]);

    return (
      <Container
        ref={mergedRef}
        tabIndex={0}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{
          paddingTop,
          paddingRight,
          paddingBottom,
          paddingLeft,
          borderColor: currentBorderColor,
          borderRadius,
          height,
          width,
          fontSize,
          color: currentFontColor,
          backgroundColor: isInteracting
            ? currentBackgroundHoverColor
            : currentBackgroundColor
        }}
      >
        <Popout
          show={isCopied}
          anchorElement={element}
          direction="upward"
          alignment="center"
          offsetTop={-4}
        >
          <CopiedTextTooltip role="tooltip">
            {messages.copied}
          </CopiedTextTooltip>
        </Popout>

        <Inner>
          <HiddenClickToCopyText _small={fontSize < 15}>
            {messages.clickToCopy}
          </HiddenClickToCopyText>
          <ClickToCopyText
            $visible={isInteracting && !isCopied}
            $small={fontSize < 15}
          >
            {messages.clickToCopy}
          </ClickToCopyText>
          <Value
            $shifted={isInteracting && !isCopied}
            style={{
              fontFamily: fontFamily?.value,
              fontWeight,
              fontStyle
            }}
          >
            {children}
          </Value>
        </Inner>
      </Container>
    );
  }
);

export const Container = styled.div`
  cursor: pointer;
  position: relative;
  border-style: dashed;
  border-width: 1px;
  border-radius: 2px;
  overflow: hidden;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease-in;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HiddenClickToCopyText = styled.div<{ _small: boolean }>`
  line-height: 1;
  font-size: ${({ _small }) => (_small ? 8 : 11)}px;
  text-transform: uppercase;
  height: 0;
  visibility: hidden;
  ${ellipsisMixin};
`;

const CopiedTextTooltip = styled.div`
  font-size: 13px;
  padding: 6px 8px;
  border-radius: 4px;
  background-color: rgb(17, 17, 17);
  color: rgb(255, 255, 255);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.3);
`;

export const Inner = styled.div`
  position: relative;
  text-align: center;
  flex: 1;
  min-width: 0;
`;

export const ClickToCopyText = styled.div<{
  $small: boolean;
  $visible: boolean;
}>`
  ${({ $small, $visible }) => css`
    line-height: 1;
    font-size: ${$small ? 8 : 11}px;
    text-transform: uppercase;
    position: absolute;
    left: 50%;
    bottom: 100%;
    transform: translate(-50%, 4px);
    opacity: ${$visible ? 1 : 0};
    transition: opacity 0.15s ease-in;
    ${ellipsisMixin};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const Value = styled.div<{ $shifted: boolean }>`
  ${({ $shifted }) => css`
    line-height: 1.32;
    transform: ${$shifted ? 'translateY(2px)' : 'translateY(0)'};
    transition: transform 0.15s ease-in;
    ${ellipsisMixin};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;
